import React, { FC, useMemo } from 'react';

import cx from 'clsx';

import { frozenInfoSelector, procedurePrioritySelector } from 'features/details';
import { useSelector } from 'react-redux';
import { staticResponseSelector } from 'features/details/response/static';
import { dynamicResponseSelector } from 'features/details/response/dynamic';
import {
  AsyncPollableState, AsyncStatus,
} from 'types';
import { ResponseGroup, ResponseGroupType } from '@x-guard/xgac-types/xgac';
import { isEqual, maxBy } from 'lodash';
import { ResponseSection } from './responseSection';
import { AlarmCreatorSection } from './alarmCreatorSection';
import styles from './style.module.scss';
import { useResponseRefetch } from './useResponseRefetch';

const zoneContainsStaticResponderId = (group: ResponseGroup, staticResponderId: string): boolean => {

  const match = (id: string | undefined) => id === staticResponderId;

  if (group.responders?.some((r) => match(r.info?._id))) return true;

  const zone = (group as ResponseGroup & { zone?: { staticResponders?: Array<{ staticResponder?: { _id?: string } }> } }).zone;
  return zone?.staticResponders?.some((sr) => match(sr.staticResponder?._id)) ?? false;

};

export type EssentialStatePick<T> = Pick<AsyncPollableState<T>, 'value' | 'status' | 'error' | 'isRetrying'>;

export const Response: FC = () => {

  const priority = useSelector(procedurePrioritySelector);
  const staticState = useSelector(staticResponseSelector, isEqual);
  const dynamicState = useSelector(dynamicResponseSelector, isEqual);
  const frozenInfo = useSelector(frozenInfoSelector);

  useResponseRefetch();

  // When static and dynamic states are merged, we have the freedom to sort the items however we want
  const mergedState = useMemo<EssentialStatePick<ResponseGroup[]>>(() => {

    const state = (value: ResponseGroup[]) => ({
      value,
      status: AsyncStatus.Fulfilled,
      error: null,
      isRetrying: false,
    });

    if (frozenInfo) {

      const aprs = frozenInfo.professionalResponders;
      const selectedApr = aprs?.length ? maxBy(aprs, (r) => new Date(r.updatedAt).getTime()) : null;
      const aprStaticResponderId = selectedApr?.staticResponder?._id;
      const aprCustomerId = selectedApr?.staticResponder?.customer?._id;

      type ZoneGroup = ResponseGroup & { zone?: { customer?: { _id?: string }; staticResponders?: Array<{ staticResponder?: { _id?: string } }> } };

      const shouldReplaceZoneWithApr = (group: ResponseGroup): boolean => {

        const isZone = `${group.type}`.toLowerCase() === `${ResponseGroupType.Zone}`.toLowerCase();
        if (!isZone) return false;
        if (aprStaticResponderId && zoneContainsStaticResponderId(group, aprStaticResponderId)) return true;
        if (aprCustomerId && (group as ZoneGroup).zone?.customer?._id === aprCustomerId) return true;
        return false;

      };

      const staticGroups: ResponseGroup[] = selectedApr
        ? (frozenInfo.staticResponse ?? []).map((group) => {

          const containsId = shouldReplaceZoneWithApr(group);

          if (containsId) {

            return {
              type: ResponseGroupType.AlarmProfessionalResponder,
              priority: group.priority,
              responders: [],
            } as ResponseGroup;

          }

          return group;

        })
        : frozenInfo.staticResponse ?? [];

      return state([...frozenInfo.dynamicResponse ?? [], ...staticGroups]);

    }

    if (!(dynamicState.status.fulfilled && staticState.status.fulfilled)) {

      return null;

    }

    return state([...dynamicState.value, ...staticState.value]);

  }, [frozenInfo, dynamicState, staticState]);

  return (
    <div className={cx(
      styles.container,
      styles[priority],
    )}>
      <AlarmCreatorSection/>
      {mergedState ? (
        <ResponseSection classNameMap={styles} state={mergedState}/>
      ) : (
        <>
          <ResponseSection classNameMap={styles} state={dynamicState}/>
          <ResponseSection classNameMap={styles} state={staticState}/>
        </>
      )}
    </div>
  );

};
